// ===================================================================
// KAIXO - Pestaña Auditoría (solo admin_general)
// ===================================================================
// Muestra el log inmutable de acciones administrativas: quién hizo
// qué, cuándo, desde dónde. Útil para detectar abusos, investigar
// incidentes y demostrar trazabilidad a inversores/auditores.
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { adminAuditLog } from '../../../services/adminApi';
import './AdminAuditSection.css';

const ACTION_LABELS = {
    area_unlock_success:      { label: 'Unlock área OK',        color: '#22c55e' },
    area_unlock_fail:         { label: 'Unlock área FAIL',      color: '#ef4444' },
    area_unlock_locked:       { label: 'Área bloqueada',        color: '#ef4444' },
    area_password_changed:    { label: 'Cambio contraseña área',color: '#3b82f6' },
    grant_role:               { label: 'Rol asignado',          color: '#8b5cf6' },
    revoke_role:              { label: 'Rol revocado',          color: '#f59e0b' },
    reset_area_password:      { label: 'Reset contraseña área', color: '#f59e0b' },
    verification_approved:    { label: 'Verificación aprobada', color: '#22c55e' },
    verification_rejected:    { label: 'Verificación rechazada',color: '#ef4444' },
    verification_info_req:    { label: 'Info pedida',           color: '#3b82f6' },
    verification_viewed:      { label: 'Verificación vista',    color: '#888' }
};

const AREA_LABELS = {
    general: 'General',
    support: 'Soporte',
    verification: 'Verificación',
    accounting: 'Contabilidad',
    maintenance: 'Mantenimiento'
};

function timeAgo(date) {
    if (!date) return '';
    const sec = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (sec < 60) return `hace ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `hace ${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h}h`;
    return `hace ${Math.floor(h / 24)}d`;
}

export default function AdminAuditSection() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterArea, setFilterArea] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 50;

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = {
                limit: PAGE_SIZE,
                skip: page * PAGE_SIZE
            };
            if (filterArea) params.area = filterArea;
            if (filterAction) params.action = filterAction;
            const { data } = await adminAuditLog(params);
            setEntries(data?.entries || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [page, filterArea, filterAction]);

    useEffect(() => { reload(); }, [reload]);

    return (
        <div className="aud-wrap">
            <div className="aud-intro">
                <p><strong>Registro inmutable de acciones administrativas.</strong></p>
                <p className="aud-intro-sub">
                    Cada acción queda registrada con IP, user-agent y timestamp. Útil para auditoría de seguridad.
                </p>
            </div>

            <div className="aud-filters">
                <select
                    className="aud-select"
                    value={filterArea}
                    onChange={e => { setFilterArea(e.target.value); setPage(0); }}
                >
                    <option value="">Todas las áreas</option>
                    {Object.entries(AREA_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
                <select
                    className="aud-select"
                    value={filterAction}
                    onChange={e => { setFilterAction(e.target.value); setPage(0); }}
                >
                    <option value="">Todas las acciones</option>
                    {Object.entries(ACTION_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                    ))}
                </select>
                <button className="aud-refresh" onClick={reload}>↻ Refrescar</button>
            </div>

            {loading && <div className="ap-loading">Cargando log…</div>}
            {error && <div className="ap-error">{error}</div>}

            {!loading && (
                <>
                    <table className="aud-table">
                        <thead>
                            <tr>
                                <th>Cuándo</th>
                                <th>Actor</th>
                                <th>Acción</th>
                                <th>Área</th>
                                <th>Objetivo</th>
                                <th>IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map(e => {
                                const meta = ACTION_LABELS[e.action] || { label: e.action, color: '#888' };
                                return (
                                    <tr key={e._id}>
                                        <td title={new Date(e.createdAt).toLocaleString()}>
                                            {timeAgo(e.createdAt)}
                                        </td>
                                        <td>
                                            <strong>{e.actorName || '—'}</strong>
                                            <div className="aud-uid">{e.actor}</div>
                                        </td>
                                        <td>
                                            <span className="aud-action-chip" style={{ background: meta.color + '22', color: meta.color }}>
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td title={e.area || ''}>{AREA_LABELS[e.area] || (e.area ? `(${e.area})` : '—')}</td>
                                        <td>
                                            {e.targetName && <div>{e.targetName}</div>}
                                            {e.target && <div className="aud-uid">{e.target}</div>}
                                            {!e.target && !e.targetName && '—'}
                                        </td>
                                        <td className="aud-ip">{e.ip || '—'}</td>
                                    </tr>
                                );
                            })}
                            {entries.length === 0 && (
                                <tr>
                                    <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                                        Sin entradas para los filtros seleccionados.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    <div className="aud-pagination">
                        <button
                            className="aud-page-btn"
                            disabled={page === 0}
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                        >← Anterior</button>
                        <span className="aud-page-info">Página {page + 1}</span>
                        <button
                            className="aud-page-btn"
                            disabled={entries.length < PAGE_SIZE}
                            onClick={() => setPage(p => p + 1)}
                        >Siguiente →</button>
                    </div>
                </>
            )}
        </div>
    );
}
